/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import * as React from 'react';
import { IModuleProps, getTelemetryObject, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import classNames from 'classnames';

import { IContractsAccountPageData } from './contracts-account-page.data';
import { IContractsAccountPageProps } from './contracts-account-page.props.autogenerated';
import LinksComponent from './components/contracts-account-page-links';
import { IContractListEntity } from '../../actions/DataServiceEntities.g';

export interface IContractsAccountPageViewProps extends IContractsAccountPageProps<IContractsAccountPageData> {
    contractsHeading: React.ReactElement;
    contractsWrapper: IModuleProps;
    contractsTable: React.ReactElement;
    contractsLinks: React.ReactElement;
}

/**
 *
 * ContractsAccountPage component
 * @extends {React.PureComponent<IContractsAccountPageProps<IContractsAccountPageData>>}
 */
class ContractsAccountPage extends React.PureComponent<IContractsAccountPageProps<IContractsAccountPageData>> {
    private readonly telemetryContent: ITelemetryContent;
    constructor(props: IContractsAccountPageProps<IContractsAccountPageData>) {
        super(props);
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );

        this._renderHeading = this._renderHeading.bind(this);
        this._getContractsRowData = this._getContractsRowData.bind(this);
        this._handleHeadingChange = this._handleHeadingChange.bind(this);
        this._renderTable = this._renderTable.bind(this);
        this._generateRow = this._generateRow.bind(this);
    }

    private _renderHeading(): React.ReactElement {
        const {
            config: { heading },
            context: { request }
        } = this.props;

        if (!heading || !heading.text) {
            return <></>;
        }

        return (
            <Msdyn365.Text
                className='eve-contracts-tile__heading'
                tag={heading.tag || 'h2'}
                text={heading.text}
                editProps={{ onEdit: this._handleHeadingChange, requestContext: request }}
            />
        );
    }

    private _getContractsRowData(): IContractListEntity[] {
        if (!this.props.data.contractList.result) {
            return [];
        }
        // @ts-expect-error
        if (this.props.data.contractList.result.contractList && this.props.data.contractList.result.contractList.length > 0) {
            // @ts-expect-error
            return this.props.data.contractList.result.contractList;
        }
        return [];
    }

    private _handleHeadingChange = (event: Msdyn365.ContentEditableEvent): string => (this.props.config.heading.text = event.target.value);

    private _renderTable(): React.ReactElement {
        const {
            resources: { manufacturerCodeLabel, contractNumberLabel, laborRateLabel, contractDescLabel, noContractsFoundText }
        } = this.props;

        const headingRow = [manufacturerCodeLabel, contractNumberLabel, laborRateLabel, contractDescLabel];
        const contractsRowData = this._getContractsRowData();
        return (
            <div>
                <ul className='contracts-table'>
                    <li className='contracts-table-header'>
                        {headingRow.map((heading, i) => (
                            <div key={heading} className={`contracts-col contracts-col-${i + 1}`}>
                                {heading === manufacturerCodeLabel ? this._renderMfrCodeHeading(i + 1) : heading}
                            </div>
                        ))}
                    </li>
                    {contractsRowData.length > 0 ? (
                        contractsRowData.map((contract, i) => (
                            <li className='contracts-table-row' key={contract.ContractCode}>
                                {this._generateRow(contract)}
                            </li>
                        ))
                    ) : (
                        <li className='contracts-table-row' key='empty'>
                            {this._generateEmptyRow(noContractsFoundText)}
                        </li>
                    )}
                </ul>
            </div>
        );
    }

    private _renderMfrCodeHeading(colNum: number): React.ReactElement {
        const {
            resources: { manufacturerCodeLabel, manufacturerCodeShortFormLabel }
        } = this.props;

        return (
            <>
                <span className={`contracts-col contracts-col-${colNum}--mobile`}>{manufacturerCodeShortFormLabel}</span>
                <span className={`contracts-col contracts-col-${colNum}--desktop`}>{manufacturerCodeLabel}</span>
            </>
        );
    }

    private _generateRow(contractData: IContractListEntity) {
        const { ManufacturerCode, Description, ContractLaborRate, ContractNotes } = contractData;
        return (
            <>
                <div className='contracts-col contracts-col-1'>
                    <p>{ManufacturerCode}</p>
                </div>
                <div className='contracts-col contracts-col-2'>
                    <p>{Description}</p>
                </div>
                <div className='contracts-col contracts-col-3'>
                    <p>{`$${ContractLaborRate.toFixed(2)}`}</p>
                </div>
                <div className='contracts-col contracts-col-4'>
                    <p>{ContractNotes}</p>
                </div>
            </>
        );
    }

    private _generateEmptyRow(noContractsFoundText: string) {
        return (
            <>
                <div className='contracts-col contracts-col-1'>
                    <p>{noContractsFoundText}</p>
                </div>
                <div className='contracts-col contracts-col-2'>
                    <p></p>
                </div>
                <div className='contracts-col contracts-col-3'>
                    <p></p>
                </div>
                <div className='contracts-col contracts-col-4'>
                    <p></p>
                </div>
            </>
        );
    }

    private _renderLink(): React.ReactElement {
        const {
            config: { links },
            context: { request }
        } = this.props;

        if (!links || links.length < 1) {
            return <></>;
        }

        return (
            <LinksComponent
                {...{ links, onTextChange: this._handleLinkTextChange, requestContext: request, telemetryContent: this.telemetryContent }}
            />
        );
    }

    private _handleLinkTextChange = (linkIndex: number) => (event: Msdyn365.ContentEditableEvent) => {
        const { links } = this.props.config;
        if (links && links[linkIndex]) {
            links[linkIndex].linkText = event.target.value;
        }
    };

    public render(): JSX.Element | null {
        const viewProps = {
            ...this.props,
            contractsHeading: this._renderHeading(),
            contractsWrapper: {
                className: classNames('eve-contracts-tile', 'contracts-container'),
                tag: 'div',
                moduleProps: this.props
            },
            contractsTable: this._renderTable(),
            contractsLinks: this._renderLink()
        };

        return this.props.renderView(viewProps);
    }
}

export default ContractsAccountPage;
